import React, { useEffect, useState } from "react";
import type { FC, PropsWithChildren } from "react";
import { ModalForm, Modal, Button } from "../../components/Modal";
import ModalHeading from "../../components/Modal/ModalHeading";
import SkeletonLoader from "../../Skeletons/IntakeFormSkeleton";
import InputWrapper from "../../components/Modal/InputWrapper";
import SliderBar from "../../components/Modal/SlideBar";
import TextAreaField from "../../components/Modal/TextAreaField";
import RadioButton from "../../components/Modal/RadioButton";
import CheckboxInput from "../../components/Modal/CheckBoxInput";
import PatientStyle from "../../styles/Patient.module.css";
import useFetchIntakeForm from "../../hooks/patients/useFetchIntakeForm";
import { IaddPatientModalStates, IntakeFormQuestion } from "./AddPatientModal";


type AddEditModalProps = PropsWithChildren<{
  showModal: boolean;
  onCloseModal?: () => void;
//   onClick?: () => void;
 handleIntakeFormData: (formattedData:IntakeFormQuestion[]) => void;
  planId: string
}>;

const IntakeFormModal: FC<AddEditModalProps> = (props) => {
    const [formState, setFormState] = useState<{ [key: string]: any }>({});
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const { getIntakeFormData, intakeForm, intakeFormLoading } =
    useFetchIntakeForm({
      planId: props.planId
    });

    useEffect(()=>{getIntakeFormData()},[props.planId])


  const updateState = (field: string, value: any) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleCheckbox = (field: string, label: string) => {
    setFormState((prev) => {
      const current = prev[field];
      const list = Array.isArray(current) ? current : [];
      const updated = list.includes(label)
        ? list.filter((item) => item !== label)
        : [...list, label];
      return { ...prev, [field]: updated };
    });
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    if (!intakeForm?.data) return true;

    const allRecords: any[] = [];
    if (intakeForm.data.questionGroupedByCategory === "no") {
      if (Array.isArray(intakeForm.data.data)) {
        allRecords.push(...intakeForm.data.data);
      }
    } else {
      if (Array.isArray(intakeForm.data.data)) {
        intakeForm.data.data.forEach((dataGroup: any) => {
          if (dataGroup.records && Array.isArray(dataGroup.records)) {
            allRecords.push(...dataGroup.records);
          }
        });
      }
    }

    allRecords.forEach((record) => {
      const value = formState[record.questionId];
      if (record.answerType !== "scale" && (value === undefined || value === null || value === "")) {
        errors[record.questionId] = "This field is required";
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const formatFormData = () => {
    const formattedData: IntakeFormQuestion[] = [];
    if (!intakeForm?.data) return formattedData;
 
    const processRecord = (record: any) => {
      const answerType = record.answerType;
      const value = formState[record.questionId];
      if (value !== undefined && value !== null && value !== "" && record.questionId) {
        const formattedItem: any = {
          question_id: record.questionId,
          question_answer: value,
          question_type: answerType,
        };
 
 
        if (answerType === "single_option") {
        
          const selectedOption = (record.option || []).find((opt: any) => opt.name === value);
          if (selectedOption && selectedOption.textboxRequired === "yes") {
            const textboxValue = formState[`${record.questionId}_${value}_textbox`];
            if (textboxValue) {
              formattedItem.more_details = textboxValue;
            }
          }
        } else if (answerType === "multiple_option") {
   
          if (Array.isArray(value)) {
            const moreDetailsObj: { [key: string]: string } = {};
            let hasDetails = false;
            
            value.forEach((optName: string) => {
              const option = (record.option || []).find((opt: any) => opt.name === optName);
              if (option && option.textboxRequired === "yes") {
                const textboxValue = formState[`${record.questionId}_${optName}_textbox`];
                if (textboxValue) {
                  moreDetailsObj[optName] = textboxValue;
                  hasDetails = true;
                }
              }
            });
            
            if (hasDetails) {
              formattedItem.more_details = moreDetailsObj;
            }
          }
        }
        
        formattedData.push(formattedItem);
      }
    };
 
    if (intakeForm.data.questionGroupedByCategory === "no") {
      if (Array.isArray(intakeForm.data.data)) {
        intakeForm.data.data.forEach(processRecord);
      }
    } else {
      if (Array.isArray(intakeForm.data.data)) {
        intakeForm.data.data.forEach((dataGroup: any) => {
          if (dataGroup.records && Array.isArray(dataGroup.records)) {
            dataGroup.records.forEach(processRecord);
          }
        });
      }
    }
    return formattedData;
  };
 

  const renderQuestionContent = (
    record: any, 
    idx: number,
    isFirstInGroup: boolean,
    categoryName?: string
  ) => {
    const answerType = record.answerType;
    const options = record.option || [];
    const inputWrapperTitle = isFirstInGroup && categoryName ? categoryName : undefined;
 
    const serialNumber = idx + 1;
    const error = formErrors[record.questionId];

    return (
      <div
        key={record._id || record.questionId || idx}
        style={{ marginBottom: "1rem" }}
      >
        {answerType === "single_option" && (
          <InputWrapper
            subTitle={`${serialNumber}. ${record.text}`}
            title={inputWrapperTitle}
            error={error}
          >
            <div
              style={{
                display: "flex",
                flexDirection:
                  options.length < 2 ? "row" : "column",
                gap: "1rem",
                marginTop: "1rem",
              }}
            >
              {options.map((opt: any) => (
                <div key={opt.key}>
                  <RadioButton
                    label={opt.name}
                    checked={
                      formState[record.questionId] === opt.name
                    }
                    onChange={() => {
                      updateState(record.questionId, opt.name);
                      
                      options.forEach((otherOpt: any) => {
                        if (otherOpt.name !== opt.name && otherOpt.textboxRequired === "yes") {
                          updateState(
                            `${record.questionId}_${otherOpt.name}_textbox`,
                            ""
                          );
                        }
                      });
                    }}
                  />
                  {opt.textboxRequired === "yes" &&
                    formState[record.questionId] === opt.name && (
                      <TextAreaField
                        rows={2}
                        placeholder="Please specify"
                        value={
                          formState[
                            `${record.questionId}_${opt.name}_textbox`
                          ] || ""
                        }
                        onChange={(e) =>
                          updateState(
                            `${record.questionId}_${opt.name}_textbox`,
                            e.target.value
                          )
                        }
                      />
                    )}
                </div>
              ))}
            </div>
          </InputWrapper>
        )}

        {answerType === "multiple_option" && (
          <InputWrapper
            subTitle={`${serialNumber}. ${record.text}`}
            title={inputWrapperTitle}
            error={error}
          >
            <div className={PatientStyle.checkbox_div_1}>
              {options.map((opt: any) => (
                <div key={opt.key}>
                  <CheckboxInput
                    label={opt.name}
                    checked={(
                      formState[record.questionId] || []
                    ).includes(opt.name)}
                    onChange={() =>
                      toggleCheckbox(record.questionId, opt.name)
                    }
                  />
                  {opt.textboxRequired === "yes" &&
                    (formState[record.questionId] || []).includes(
                      opt.name
                    ) && (
                      <TextAreaField
                        rows={2}
                        placeholder="Please specify"
                        value={
                          formState[
                            `${record.questionId}_${opt.name}_textbox`
                          ] || ""
                        }
                        onChange={(e) =>
                          updateState(
                            `${record.questionId}_${opt.name}_textbox`,
                            e.target.value
                          )
                        }
                      />
                    )}
                </div>
              ))}
            </div>
          </InputWrapper>
        )}

        {answerType === "textarea" && (
          <InputWrapper
            subTitle={`${serialNumber}. ${record.text}`}
            title={inputWrapperTitle}
            error={error}
          >
            <TextAreaField
              rows={2}
              value={formState[record.questionId] || ""}
              onChange={(e) =>
                updateState(record.questionId, e.target.value)
              }
            />
          </InputWrapper>
        )}

        {answerType === "scale" && (
          <InputWrapper
            subTitle={`${serialNumber}. ${record.text}`}
            title={inputWrapperTitle}
            error={error}
          >
            <SliderBar
              scale
              value={formState[record.questionId] || 0}
              onChange={(value: number) =>
                updateState(record.questionId, value)
              }
              customLabel
              min={record.scale?.min?.key ? parseInt(record.scale.min.key) : 0}
              max={record.scale?.max?.key ? parseInt(record.scale.max.key) : 10}
              leftLabel={record.scale?.min?.name || "Min"}
              rightLabel={record.scale?.max?.name || "Max"}
            />
          </InputWrapper>
        )}
      </div>
    );
  };

  const handleFormSubmit = () => {
    if (!validateForm()) {
      return;
    }
    const formattedData = formatFormData();
    // props.setAddEditModalStates((prevState) => ({
    //   ...prevState,
    //   intakeForm: formattedData,
    // }));
    props.handleIntakeFormData(formattedData)
    props.onCloseModal?.();
  };

  return (
    <Modal
      show={props.showModal}
      modalClassName={PatientStyle.customEnrollModal}
    >
      <button
        onClick={props.onCloseModal}
        style={{
          position: 'absolute',
          top: '1.5rem',
          right: '1.5rem',
          background: 'transparent',
          border: 'none',
          fontSize: '2.4rem',
         
          cursor: 'pointer',
          padding: '0',
          lineHeight: '1',
          zIndex: 1051, 
        }}
        aria-label="Close"
      >
        &times;
      </button>
      <ModalHeading
        heading={intakeForm?.data.templateName || "Intake Form"}
        customClass={[PatientStyle.customEnrollHeading].join(" ")}
    
      />
      {intakeFormLoading ? (
        <SkeletonLoader />
      ) : (
        <ModalForm
          customClass={PatientStyle.enroll_ModalForm}
          id="confirm-form"
        >
          <div
            className={PatientStyle.enroll_ModalForm_inner}
            style={{ marginBottom: "1rem" }}
          >
            {intakeForm?.data &&
              (intakeForm.data.questionGroupedByCategory === "no"
                ? Array.isArray(intakeForm.data.data) && intakeForm.data.data.map((record: any, idx: number) =>
                    renderQuestionContent(
                      record,
                      idx,
                      false, 
                      undefined
                    )
                  )
                : Array.isArray(intakeForm.data.data) && intakeForm.data.data.map((dataGroup: any) => (
                    <div
                      key={dataGroup._id}
                      className={PatientStyle.custom_content}
                    >
                      {dataGroup.records && Array.isArray(dataGroup.records) && dataGroup.records.map((record: any, idx: number) =>
                        renderQuestionContent(
                          record,
                          idx,
                          idx === 0,
                          dataGroup.name
                        )
                      )}
                    </div>
                  )))}        
            {/* End of conditional rendering logic */}
          </div>
          <div className={PatientStyle.custom_btn_box}>
            <Button
              label="Cancel"
              icon="icon-close-cross"
              span={false}
              iconStyle={{ height: "1.5rem", width: "1.5rem" }}
              customClass={PatientStyle.cancel_btn}
              onClick={props.onCloseModal}
              form="confirm-form"
            />
            <Button
              icon="icon-save"
              label="Save"
              span={false}
              iconStyle={{ height: "1.5rem", width: "1.5rem" }}
              customClass={PatientStyle.save_data_btn}
              onClick={handleFormSubmit}
            />
          </div>
        </ModalForm>
      )}
    </Modal>
  );
};

export default IntakeFormModal;
